import React, { useState, useEffect } from "react";
import Image from "../../../components/AppImage";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useConfig } from "../../../context/ConfigContext";
import { storage } from "../../../lib/firebase";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { formatNum, formatNumKgT, formatNumMil, formatNumMilSplit } from "../../../components/utils/format";
import { roundTo } from "../../../components/utils/mathUtils";

const ProjectModal = ({ project, isOpen, onClose, onRequestQuote }) => {
  const config = useConfig();
  const {
    FACT_CO2_PROMEDIO_ANUAL,
    FACT_ARBOLES_PROM_ANUAL,
    FACT_CARS_OFF_PROM_ANUAL,
    FACT_CARBON_PROM_ANUAL,
  } = config;
  const [gallery, setGallery] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchGallery = async () => {
      if (isOpen && project?.carpeta) {
        setLoadingGallery(true);
        try {
          const folderRef = ref(storage, `SolarValley/Proyectos/${project.carpeta}`);
          const res = await listAll(folderRef);
          const urls = await Promise.all(res.items.map((item) => getDownloadURL(item)));
          setGallery(urls);
        } catch (error) {
          console.error("Error fetching gallery:", error);
        } finally {
          setLoadingGallery(false);
        }
      } else {
        setGallery([]);
      }
      setCurrentImageIndex(0);
    };

    fetchGallery();
  }, [isOpen, project, storage]);

  if (!isOpen || !project) return null;

  const formatNumber = (num) => {
    return new Intl.NumberFormat("es-GT")?.format(num);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === gallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? gallery.length - 1 : prev - 1
    );
  };

  const calculateEnvironmentalImpact = () => {
    const annualGeneration = project?.generaAnual || 0;
    const co2Reduction = annualGeneration * FACT_CO2_PROMEDIO_ANUAL; // toneladas de CO2 per kWh in Guatemala
    const treesEquivalent = co2Reduction * FACT_ARBOLES_PROM_ANUAL; // toneladas de CO2 absorbed per tree per year
    const carsOffRoad = co2Reduction / FACT_CARS_OFF_PROM_ANUAL; // toneladas de CO2 per car per year

    return {
      annualGeneration: annualGeneration,
      annualCO2Reduction: co2Reduction,
      treesEquivalent: roundTo(treesEquivalent, 3),
      carsOffRoad: roundTo(carsOffRoad, 3),
      coalAvoided: roundTo(co2Reduction * FACT_CARBON_PROM_ANUAL), // tons of coal
    };
  };

  const environmentalImpact = calculateEnvironmentalImpact();
  const infoDiaria = formatNumMilSplit(project?.generaDiaria * 1000, 2)
  const infoMensual = formatNumMilSplit(project?.generaMensual * 1000, 0)
  const infoAnual = formatNumMilSplit(project?.generaAnual * 1000, 2)


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden solar-shadow-elevated">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              {project?.nombreProyecto}
            </h2>
            <div className="flex items-center text-muted-foreground mt-1">
              <Icon name="MapPin" size={16} className="mr-1" />
              {project?.ubicacion}
              <span className="mx-2">•</span>
              <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm">
                {project?.tipo}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" />
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row max-h-[calc(90vh-80px)] overflow-y-auto">
          {/* Image Gallery */}
          <div className="lg:w-1/2 relative bg-gray-50 border-b lg:border-b-0 lg:border-r">
            {loadingGallery ? (
              <div className="flex items-center justify-center h-64 lg:h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : gallery.length > 0 ? (
              <div className="relative flex flex-col lg:h-full">
                <div className="relative h-[300px] sm:h-[400px] lg:flex-1 lg:h-auto">
                  <Image
                    src={gallery[currentImageIndex]}
                    alt={`${project?.nombreProyecto} - Imagen ${currentImageIndex + 1}`}
                    className="w-full h-full object-contain"
                  />

                  {/* Navigation Arrows */}
                  {gallery.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center solar-transition"
                      >
                        <Icon name="ChevronLeft" size={20} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center solar-transition"
                      >
                        <Icon name="ChevronRight" size={20} />
                      </button>
                    </>
                  )}

                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs font-medium z-10">
                    {currentImageIndex + 1} / {gallery.length}
                  </div>
                </div>

                {/* Thumbnail Strip */}
                <div className="p-4 border-t">
                  <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                    {gallery.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 solar-transition ${index === currentImageIndex
                          ? "border-primary"
                          : "border-transparent hover:border-muted"
                          }`}
                      >
                        <Image
                          src={image}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-12 text-muted-foreground">
                <Icon name="ImageOff" size={48} className="mb-4 opacity-20" />
                <p>No hay imágenes disponibles para este proyecto</p>
              </div>
            )}
          </div>

          {/* Details Panel */}
          <div className="lg:w-1/2 flex flex-col p-6 lg:p-10">
            <div className="space-y-8">
              {/* Energía Generada */}
              <div>
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center">
                  <Icon name="Zap" size={20} className="mr-2 text-primary" />
                  Energía Generada
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-primary/5 rounded-xl border border-primary/10 flex flex-col justify-center">
                    <div className="text-[10px] text-primary font-bold mb-1 uppercase tracking-wider text-muted-foreground">Diaria</div>
                    <div className="text-2xl font-bold text-foreground flex items-baseline gap-1">
                      {infoDiaria.numero}
                      <span className="text-xs font-medium text-muted-foreground">{infoDiaria.letra}W</span>
                    </div>
                  </div>
                  <div className="p-4 bg-accent/5 rounded-xl border border-accent/10 flex flex-col justify-center">
                    <div className="text-[10px] text-accent font-bold mb-1 uppercase tracking-wider text-muted-foreground">Generación Mensual</div>
                    <div className="text-2xl font-bold text-foreground flex items-baseline gap-1">
                      {infoMensual.numero}
                      <span className="text-xs font-medium text-muted-foreground">{infoMensual.letra}W</span>
                    </div>
                  </div>
                  <div className="p-4 bg-success/5 rounded-xl border border-success/10 flex flex-col justify-center">
                    <div className="text-[10px] text-success font-bold mb-1 uppercase tracking-wider text-muted-foreground">Energía Anual</div>
                    <div className="text-2xl font-bold text-foreground flex items-baseline gap-1">
                      {infoAnual.numero}
                      <span className="text-xs font-medium text-muted-foreground">{infoAnual.letra}W</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* System info */}
              <div>
                <h3 className="text-lg font-bold text-foreground mb-6 flex items-center">
                  <Icon name="Settings" size={20} className="mr-2 text-primary" />
                  Especificaciones del Sistema
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                  <div className="flex flex-col border-l-2 border-primary/10 pl-4 transition-all hover:border-primary">
                    <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider mb-1">Paneles Solares</span>
                    <span className="text-sm font-bold text-foreground leading-snug">
                      {project?.marcaPaneles || "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col border-l-2 border-primary/10 pl-4 transition-all hover:border-primary">
                    <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider mb-1">Inversor Central</span>
                    <span className="text-sm font-bold text-foreground leading-snug">
                      {project?.marcaInversor || "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col border-l-2 border-primary/10 pl-4 transition-all hover:border-primary">
                    <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider mb-1">Estructura de Montaje</span>
                    <span className="text-sm font-bold text-foreground leading-snug">
                      {project?.montaje || "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col border-l-2 border-primary/10 pl-4 transition-all hover:border-primary">
                    <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider mb-1">Sistema de Monitoreo</span>
                    <span className="text-sm font-bold text-foreground leading-snug">
                      {project?.monitoreo || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Environmental Impact */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center">
                  <Icon name="Leaf" size={20} className="mr-2 text-success" />
                  Impacto Ambiental Estimado
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                      <Icon name="Cloud" size={24} />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">CO₂ Reducido</div>
                      <div className="text-xl font-bold">{formatNumKgT(environmentalImpact?.annualCO2Reduction, 0)} por año</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                      <Icon name="TreeDeciduous" size={24} />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Árboles Plantados</div>
                      <div className="text-xl font-bold">{formatNum(environmentalImpact?.treesEquivalent, 0)} por año</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action */}
              <div className="pt-4">
                <Button
                  variant="default"
                  onClick={() => onRequestQuote(project)}
                  iconName="Calculator"
                  iconPosition="left"
                  className="w-full bg-cta-orange hover:bg-cta-orange/90 py-6 text-lg"
                >
                  Cotizar Sistema Similar
                </Button>
                <Button variant="ghost" onClick={onClose} className="w-full mt-2">
                  Cerrar Detalles
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
